import { useEffect, useState } from "react";
import { Phone, Smartphone, Mail, MapPin, Clock, Send } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "@/lib/api";
import { ENQUIRY_EVENT } from "@/lib/enquiry";

export default function ContactSection({ settings }) {
    const [form, setForm] = useState({ name: "", email: "", phone: "", subject: "", message: "" });
    const [submitting, setSubmitting] = useState(false);

    useEffect(() => {
        const handler = (e) => {
            const { subject = "", message = "" } = e.detail || {};
            setForm((f) => ({ ...f, subject, message }));
        };
        window.addEventListener(ENQUIRY_EVENT, handler);
        return () => window.removeEventListener(ENQUIRY_EVENT, handler);
    }, []);

    const update = (k) => (e) => setForm({ ...form, [k]: e.target.value });

    const submit = async (e) => {
        e.preventDefault();
        if (!form.name.trim() || !form.email.trim() || !form.message.trim()) {
            toast.error("Please fill in your name, email and message");
            return;
        }
        if (form.name.trim().length < 2) {
            toast.error("Please enter your full name");
            return;
        }
        if (form.message.trim().length < 5) {
            toast.error("Please write a longer message");
            return;
        }
        setSubmitting(true);
        try {
            const { data } = await api.post("/contact", form);
            if (data && data.saved) {
                toast.success("Message received — we'll reply within 30 minutes.");
            } else {
                toast.warning("We had trouble saving your message. Please call us on the number above.");
            }
            setForm({ name: "", email: "", phone: "", subject: "", message: "" });
        } catch (err) {
            toast.error(
                formatApiErrorDetail(err?.response?.data?.detail) ||
                "Could not send your message. Please call us on the number above."
            );
        } finally {
            setSubmitting(false);
        }
    };

    if (!settings) {
        return (
            <section
                id="contact"
                data-testid="contact-section-loading"
                className="bg-[#18181a] py-20 md:py-28 border-t border-white/10 text-center text-zinc-500"
            >
                Loading contact info…
            </section>
        );
    }
    const mapSrc = `https://www.google.com/maps?q=${encodeURIComponent(settings.map_query || settings.address || "")}&output=embed`;

    return (
        <section
            id="contact"
            data-testid="contact-section"
            className="relative bg-[#18181a] py-20 md:py-28 border-t border-white/10"
        >
            <div className="max-w-7xl mx-auto px-5 md:px-10 grid lg:grid-cols-2 gap-12">
                <div>
                    <div className="text-[10px] uppercase tracking-[0.3em] text-[#d52b1e] mb-3">
                        Get In Touch
                    </div>
                    <h2 className="font-display uppercase text-4xl sm:text-5xl tracking-tight mb-4">
                        Looking for a part?
                    </h2>
                    <p className="text-zinc-400 max-w-lg">
                        Send us a message or call directly. We'll confirm part availability,
                        condition and shipping options within 30 minutes.
                    </p>

                    <div className="mt-10 space-y-5">
                        <Item icon={<Phone />} label="Phone" value={settings.phone || "—"} href={settings.phone ? `tel:${settings.phone.replace(/\s/g, "")}` : null} testid="contact-phone" />
                        {settings.mobile && (
                            <Item icon={<Smartphone />} label="Mobile" value={settings.mobile} href={`tel:${settings.mobile.replace(/\s/g, "")}`} testid="contact-mobile" />
                        )}
                        <Item icon={<Mail />} label="Email" value={settings.email || "—"} href={settings.email ? `mailto:${settings.email}` : null} testid="contact-email" />
                        <Item icon={<MapPin />} label="Address" value={settings.address || "—"} testid="contact-address" />
                        <div className="flex gap-4 items-start">
                            <div className="bg-[#d52b1e] text-white w-10 h-10 flex items-center justify-center flex-shrink-0">
                                <Clock className="w-4 h-4" />
                            </div>
                            <div>
                                <div className="text-[10px] uppercase tracking-[0.3em] text-zinc-500">
                                    Opening Hours
                                </div>
                                <div className="mt-1 space-y-0.5">
                                    {(settings.hours || []).map((h) => (
                                        <div key={h.day} className="text-sm">
                                            <span className="text-zinc-400">{h.day}: </span>
                                            <span className="text-white">{h.hours}</span>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        </div>
                    </div>

                    <div className="mt-10 border border-white/10">
                        <iframe
                            title="Auto Europarts location"
                            src={mapSrc}
                            className="w-full h-64"
                            loading="lazy"
                        />
                    </div>
                </div>

                <form
                    onSubmit={submit}
                    className="bg-[#0a0a0a] border border-white/10 p-6 md:p-8"
                    data-testid="contact-form"
                >
                    <div className="text-[10px] uppercase tracking-[0.3em] text-zinc-500 mb-6">
                        Send a Message
                    </div>
                    <div className="space-y-7">
                        <input
                            value={form.name}
                            onChange={update("name")}
                            placeholder="Your name"
                            className="industrial-input"
                            required
                            data-testid="contact-name-input"
                        />
                        <input
                            value={form.email}
                            onChange={update("email")}
                            type="email"
                            placeholder="Email address"
                            className="industrial-input"
                            required
                            data-testid="contact-email-input"
                        />
                        <input
                            value={form.phone}
                            onChange={update("phone")}
                            placeholder="Phone (optional)"
                            className="industrial-input"
                            data-testid="contact-phone-input"
                        />
                        <input
                            value={form.subject}
                            onChange={update("subject")}
                            placeholder="Subject (e.g. Audi A4 B8 headlight)"
                            className="industrial-input"
                            data-testid="contact-subject-input"
                        />
                        <textarea
                            value={form.message}
                            onChange={update("message")}
                            placeholder="Which part are you looking for?"
                            rows={5}
                            className="industrial-input resize-none"
                            required
                            data-testid="contact-message-input"
                        />
                    </div>
                    <button
                        type="submit"
                        disabled={submitting}
                        className="mt-8 bg-[#d52b1e] hover:bg-[#f03022] disabled:opacity-60 text-white px-7 py-3.5 uppercase text-xs tracking-[0.25em] font-bold inline-flex items-center gap-2 transition-colors"
                        data-testid="contact-submit-btn"
                    >
                        {submitting ? "Sending..." : "Send message"}
                        <Send className="w-4 h-4" />
                    </button>
                </form>
            </div>
        </section>
    );
}

function Item({ icon, label, value, href, testid }) {
    const content = (
        <>
            <div className="bg-[#d52b1e] text-white w-10 h-10 flex items-center justify-center flex-shrink-0 [&_svg]:w-4 [&_svg]:h-4">
                {icon}
            </div>
            <div>
                <div className="text-[10px] uppercase tracking-[0.3em] text-zinc-500">{label}</div>
                <div className="text-white mt-1">{value}</div>
            </div>
        </>
    );
    return (
        <div className="flex gap-4 items-start" data-testid={testid}>
            {href ? (
                <a href={href} className="contents hover:opacity-90">
                    {content}
                </a>
            ) : (
                content
            )}
        </div>
    );
}
